import { useMessagesStore } from "@/atoms/playground";
import { IMessage, MessagePart } from "@/types";
import { Mention } from "@/types/agent-tools";
import { Box, Chip } from "@mui/material";
import { createCodePlugin } from "@streamdown/code";
import { CircleSmall, File, FolderOpen, Workflow, Wrench } from "lucide-react";
import { Streamdown } from "streamdown";
import type { MermaidConfig } from "@streamdown/mermaid";
import type { DiagramPlugin } from "streamdown";
import { useTranslation } from "react-i18next";
import { ThinkingBlock } from "./thinking-block";
import { SourceEntry, SourcesList } from "./sources-list";

type MessageReferences = ReturnType<
  typeof useMessagesStore.getState
>["references"];

const REFERENCE_TYPE_MAPPINGS: Record<string, string> = {
  document: "document",
  collection: "collection",
  retrieve_from_collection: "collection",
  // The run_flow tool marker (@run_flow[id]) resolves against the "flow"
  // reference bucket returned by the API.
  run_flow: "flow",
};

const normalizeReferenceType = (type: string) =>
  REFERENCE_TYPE_MAPPINGS[type.toLowerCase()] || type.toLowerCase();

const referenceIcons: Record<string, JSX.Element> = {
  document: <File />,
  collection: <FolderOpen />,
  flow: <Workflow />,
};

const ReferenceChip = ({
  type,
  id,
  range,
  references,
}: {
  type: string;
  id: string;
  range?: string;
  references: MessageReferences;
}) => {
  const normalizedType = normalizeReferenceType(type);
  const referenceData = references?.[normalizedType]?.[id];

  // Fall back to the raw id when the reference payload hasn't been streamed
  // (no matching `reference` event), so an unresolved reference is still
  // visible instead of rendering as empty space.
  const label = referenceData?.name ?? id;

  return (
    <Chip
      icon={referenceIcons[normalizedType] || <Wrench />}
      color="primary"
      variant={referenceData ? "filled" : "outlined"}
      label={range ? `${label} (pages:${range})` : `${label}`}
    ></Chip>
  );
};

const escapeAttr = (str: string) =>
  str
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const replaceReferences = (text: string) => {
  return text.replace(
    /`{0,1}@(\w+)\[([^\]]*?)\](?:\(([^)]*?)\))?`{0,1}/g,
    (_, type, id, range) => {
      const safeType = escapeAttr(type);
      const safeId = escapeAttr(id);
      const safeRange = range ? escapeAttr(range) : "";
      // Use `refid` rather than `id`: rehype-sanitize clobber-prefixes the
      // `id` attribute (e.g. "user-content-<id>"), which breaks the lookup.
      return range
        ? `<reference type="${safeType}" refid="${safeId}" range="${safeRange}"></reference>`
        : `<reference type="${safeType}" refid="${safeId}"></reference>`;
    },
  );
};

const removeThinking = (text: string) =>
  text
    .replace(/<think>[\s\S]*?<\/think>/g, "")
    .replace(/<think>[\s\S]*$/, "");

export const extractThinking = (text: string) => {
  const matches = Array.from(
    text.matchAll(/<think>([\s\S]*?)(?:<\/think>|$)/g),
  );
  return matches
    .map((match) => match[1])
    .join("\n\n")
    .trim();
};

export const hasOpenThinkTag = (text: string) => {
  const opens = (text.match(/<think>/g) || []).length;
  const closes = (text.match(/<\/think>/g) || []).length;
  return opens > closes;
};

// pulls a trailing "### Sources" numbered-link section out of the markdown
// so it can be rendered as source cards instead of a raw list
const splitSources = (
  text: string,
): { body: string; sources: Array<SourceEntry> } => {
  const headingMatch = /\n#{2,4}\s*(?:Sources|المصادر)\s*:?\s*\n([\s\S]*)$/i.exec(
    text,
  );
  if (!headingMatch) return { body: text, sources: [] };

  const sources: Array<SourceEntry> = [];
  // greedy title match so bracketed titles like "[PDF] Foo" survive
  const linePattern = /^\s*\d+\.\s*\[(.+)\]\(([^)\s]+)\)\s*$/gm;
  let match: RegExpExecArray | null;
  while ((match = linePattern.exec(headingMatch[1])) !== null) {
    sources.push({ title: match[1], url: match[2] });
  }

  if (sources.length === 0) return { body: text, sources: [] };
  return { body: text.slice(0, headingMatch.index).trimEnd(), sources };
};

const renderReferences = (text: string, references: MessageReferences) => {
  const pattern = /`{0,1}@(\w+)\[([^\]]*?)\](?:\(([^)]*?)\))?`{0,1}/g;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    const [full, type, id, range] = match;
    // Push preceding plain text
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    nodes.push(
      <ReferenceChip
        key={`${type}-${id}-${range ?? ""}-${match.index}`}
        type={type}
        id={id}
        range={range}
        references={references}
      />,
    );

    lastIndex = match.index + full.length;
  }

  // Push remaining plain text
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
};

const removeThinkBlock = (text: string) => {
  return text.replace(/<think>[\s\S]*?<\/think>/g, "").trim();
};

const isArabic = (text: string) => {
  const cleanText = removeThinkBlock(text);

  const arabicPattern = /[\u0600-\u06FF]/g;
  const arabicMatches = cleanText.match(arabicPattern);
  const arabicCharCount = arabicMatches ? arabicMatches.length : 0;

  const totalCharCount = cleanText.replace(/\s/g, "").length;

  return totalCharCount > 0 && arabicCharCount / totalCharCount > 0.5;
};

const buildMentionReferences = (mentions?: Mention[]) => {
  if (!mentions?.length) {
    return {};
  }

  return mentions.reduce<Record<string, Record<string, { name: string }>>>(
    (acc, mention) => {
      // Mirror the marker type produced by formatMentionForApi: document and
      // collection keep their type; every other tool (e.g. run_flow) is keyed
      // by its toolId, which is what `@run_flow[id]` resolves against.
      const markerType =
        mention.type === "document" || mention.type === "collection"
          ? mention.type
          : mention.toolId;
      const id = mention.collectionId || mention.toolId;
      const name = mention.collectionName || mention.toolName;

      if (!markerType || !id || !name) {
        return acc;
      }

      const normalizedType = normalizeReferenceType(markerType);
      acc[normalizedType] = {
        ...(acc[normalizedType] || {}),
        [id]: {
          name,
        },
      };

      return acc;
    },
    {},
  );
};

const mergeReferences = (
  base: MessageReferences,
  fallback: Record<string, Record<string, { name: string }>>,
) => {
  const merged = { ...(base || {}) };

  for (const category of Object.keys(fallback)) {
    merged[category] = {
      ...(merged[category] || {}),
      ...fallback[category],
    };
  }

  return merged;
};

const code = createCodePlugin({
  themes: ["github-light", "github-dark"],
});

let mermaidPluginPromise: Promise<DiagramPlugin> | undefined;

const loadMermaidPlugin = async () => {
  mermaidPluginPromise ??= import("@streamdown/mermaid").then(
    ({ mermaid }) => mermaid,
  );
  return mermaidPluginPromise;
};

const mermaid: DiagramPlugin = {
  name: "mermaid",
  type: "diagram",
  language: "mermaid",
  getMermaid(config?: MermaidConfig) {
    let currentConfig = config;

    return {
      initialize(nextConfig: MermaidConfig) {
        currentConfig = nextConfig;
      },
      async render(id: string, source: string) {
        const plugin = await loadMermaidPlugin();
        return plugin.getMermaid(currentConfig).render(id, source);
      },
    };
  },
};

export const Message = ({
  message,
  hideThinking,
  ...props
}: {
  message: MessagePart;
  hideThinking?: boolean;
} & IMessage) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const { role } = props;
  const references = useMessagesStore((state) => state.references);
  const messageReferences = mergeReferences(
    references,
    buildMentionReferences(props.metadata?.mentions as Mention[] | undefined),
  );
  const images =
    typeof message !== "string"
      ? message.filter((message) => message.type === "image_url")
      : [];
  const messages =
    typeof message !== "string"
      ? message.filter((message) => message.type === "text")
      : [];

  const isMessageArabic =
    typeof message === "string"
      ? isArabic(message)
      : messages.some((m) => isArabic(m.text));

  const Reference = ({
    type,
    refid,
    range,
  }: {
    type: string;
    refid: string;
    range?: string;
  }) => (
    <ReferenceChip
      type={type}
      id={refid}
      range={range}
      references={messageReferences}
    />
  );

  const thinkingText =
    role === "assistant" && typeof message === "string"
      ? extractThinking(message)
      : "";

  const { body: assistantBody, sources } =
    role === "assistant" && typeof message === "string"
      ? splitSources(removeThinking(message))
      : { body: "", sources: [] };

  return role === "assistant" ? (
    <div className="min-w-0">
      {!hideThinking && thinkingText && (
        <ThinkingBlock
          text={thinkingText}
          isActive={typeof message === "string" && hasOpenThinkTag(message)}
        />
      )}
      <Streamdown
        plugins={{ code, mermaid }}
        shikiTheme={["github-light", "github-dark"]}
        allowedTags={{
          reference: ["type", "refid", "range"],
          think: [],
          loading: [],
        }}
        components={{
          reference: Reference,
          think: ({ children }) => (
            <span className="text-sm text-muted">{children}</span>
          ),
          loading: () => (
            <CircleSmall className="ml-2 rtl:ml-0 rtl:mr-2 fill-foreground animate-ping" />
          ),
        }}
        className={`[&>:last-child]:mb-0 ${isMessageArabic ? "text-right" : "text-left"}`}
        dir={isMessageArabic ? "rtl" : "ltr"}
      >
        {replaceReferences(assistantBody)}
      </Streamdown>
      <SourcesList sources={sources} />
    </div>
  ) : (
    <Box
      sx={{
        backgroundColor: role === "user" ? "var(--user-message-bg)" : "",
        color: role === "user" ? "var(--user-message-text)" : undefined,
        alignSelf:
          role === "user" ? (isRTL ? "flex-start" : "flex-end") : "flex-start",
        padding: "12px",
        borderRadius: "8px",
        maxWidth: { xs: "90%", sm: "80%", md: "70%" },
        wordWrap: "break-word",
        overflowWrap: "break-word",
        whiteSpace: "pre-wrap",
        boxShadow: role === "user" ? "var(--message-shadow)" : "",
      }}
      className={isMessageArabic ? "text-right" : "text-left"}
      dir={isMessageArabic ? "rtl" : "ltr"}
    >
      {typeof message === "string" ? (
        renderReferences(message, messageReferences)
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            overflow: "scroll",
          }}
        >
          {images.length > 0 && (
            <Box
              sx={{
                display: "inline-flex",
                maxHeight: "300px",
                gap: "10px",
                width: "fit-content",
              }}
            >
              {images.map((image, index) => (
                <img
                  key={index}
                  style={{
                    objectFit: "contain",
                    borderRadius: "10px",
                    maxHeight: "300px",
                    maxWidth: "100%",
                    width: "auto",
                  }}
                  src={image.image_url.url}
                  alt={image.type}
                />
              ))}
            </Box>
          )}
          <Box>
            {messages.map((message, index) => (
              <p key={index} style={{ margin: "0 0 8px 0" }}>
                {renderReferences(message.text, messageReferences)}
              </p>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
